import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Button } from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { Subscription, SubscriptionCreateRequest, SubscriptionPatchRequest } from '@/api/bol-notifications';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  createSubscriptionColumns,
  defaultSubscriptionColumnVisibilityModel,
  SubscriptionCreateDialog,
  SubscriptionEditDialog,
  SubscriptionDeleteDialog
} from '@/components';

import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useSubscriptions,
  usePermissions,
  getActionTooltip
} from '@/hooks';

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(defaultSubscriptionColumnVisibilityModel);

  // Use custom hooks
  const {
    subscriptions,
    loading,
    selectedSubscription,
    setSelectedSubscription,
    fetchSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    total
  } = useSubscriptions();

  const { 
    selectedItem: selectedSubscriptionForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection({ 
    data: subscriptions, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchSubscriptions(page, pageSize);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarType,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleCreateSubscription = async (data: SubscriptionCreateRequest) => {
    try {
      await createSubscription(data);
      setCreateDialogOpen(false);
      fetchSubscriptions(pagination.page, pagination.pageSize);
    } catch (error) {
      // Error handling is done in the hook
      console.error('Subscription creation error:', error);
    }
  };

  const handleEditSubscription = async (data: SubscriptionPatchRequest) => {
    if (selectedSubscription?.id) {
      try {
        await updateSubscription(selectedSubscription.id, data);
        setSelectedSubscription(null);
        setEditDialogOpen(false);
        fetchSubscriptions(pagination.page, pagination.pageSize);
      } catch (error) {
        // Error handling is done in the hook
        console.error('Subscription update error:', error);
      }
    }
  };

  const handleDeleteSubscription = async () => {
    if (selectedSubscription?.id) {
      try {
        await deleteSubscription(selectedSubscription.id);
        setSelectedSubscription(null);
        setDeleteDialogOpen(false);
        fetchSubscriptions(pagination.page, pagination.pageSize);
      } catch (error) {
        // Error handling is done in the hook
        console.error('Subscription deletion error:', error);
      }
    }
  };

  const handleViewConsumers = (subscriptionId: string) => {
    navigate(`/consumers?subscriptionId=${subscriptionId}`);
  };

  const handleEditClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchSubscriptions(pagination.page, pagination.pageSize);
  };



  const columns = createSubscriptionColumns({
    canEdit: can.manageSubscriptions,
    canDelete: can.manageSubscriptions,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onViewConsumers: handleViewConsumers,
    editTooltip: !can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : '',
    deleteTooltip: !can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return (
    <PageLayout 
      title="Subscriptions Management"
      selectedItemInfo={selectedSubscriptionForDetail ? `Selected: ${selectedSubscriptionForDetail.name}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <Tooltip title="Refresh subscriptions list">
          <IconButton
            onClick={handleRefresh}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Tooltip title={!can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<Add />}
              disabled={!can.manageSubscriptions}
            >
              Create Subscription
            </Button>
          </span>
        </Tooltip>
      }
      sidePanel={selectedSubscriptionForDetail ? (
        <JsonDetailPanel
          title="Subscription Details"
          data={selectedSubscriptionForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => showError('Failed to copy JSON to clipboard')}
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={subscriptions}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model: React.SetStateAction<Record<string, boolean>>) => setColumnVisibilityModel(model)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getRowId={(row: { id: any; }) => row.id}
          columnOrderStorageKey="subscriptions-columns"
        />
      </Box>

      {/* Create Subscription Dialog */}
      <SubscriptionCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSubscription}
      />

      {/* Edit Subscription Dialog */}
      {selectedSubscription && (
        <SubscriptionEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSubmit={handleEditSubscription}
          subscription={selectedSubscription}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <SubscriptionDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteSubscription}
        subscription={selectedSubscription}
      />

    </PageLayout>
  );
};

export default SubscriptionsPage;
